import { formatSessionTime, getSessionHours, getSessionPrice } from './bookingHelpers';

const ChevronIcon = ({ expanded }) => (
  <svg
    className={`h-5 w-5 shrink-0 text-gray-500 transition-transform ${expanded ? 'rotate-180' : ''}`}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

const BookSessionReviewStep = ({
  sessionsGroupedBySubject,
  expandedSubjects,
  isSubjectExpanded,
  onToggleSubject,
  onEditSubject,
  tutorRate,
  bookingError,
}) => (
  <div className="space-y-6">
    <div>
      <h2 className="text-2xl font-bold text-gray-900">Review Your Booking</h2>
      <p className="mt-1 text-sm text-gray-600">
        Confirm the session details before submitting your request.
      </p>
    </div>

    <div className="rounded-lg border border-blue-100 bg-blue-50 p-4 text-sm text-gray-700">
      <p className="font-semibold text-brand-dark-blue">How payment works</p>
      <p className="mt-1">
        A temporary authorization may be placed on your card. You are only charged for sessions the
        tutor confirms.
      </p>
    </div>

    <div className="space-y-4">
      {sessionsGroupedBySubject.map((group, index) => {
        const expanded = isSubjectExpanded(group.subjectName, index);
        return (
          <div key={group.subjectName} className="overflow-hidden rounded-xl border border-gray-200 bg-white">
            <button
              type="button"
              onClick={() => onToggleSubject(group.subjectName, index)}
              className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left hover:bg-gray-50"
              aria-expanded={expanded}
            >
              <div className="min-w-0">
                <p className="text-lg font-semibold text-gray-900">{group.subjectName}</p>
                {!expanded && (
                  <p className="text-sm text-gray-500">
                    {group.sessions.length} session{group.sessions.length !== 1 ? 's' : ''}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-3">
                <span className="text-base font-semibold text-brand-dark-blue">
                  ${group.total.toFixed(2)}
                </span>
                <ChevronIcon expanded={expanded} />
              </div>
            </button>

            {expanded && (
              <div className="border-t border-gray-200 px-5 pb-5">
                <div className="flex items-center justify-between gap-3 py-3">
                  <p className="text-sm font-medium text-gray-700">
                    {group.sessions.length} Session{group.sessions.length !== 1 ? 's' : ''} Requested
                  </p>
                  <button
                    type="button"
                    onClick={() => onEditSubject(group.subjectId)}
                    className="text-sm font-semibold text-brand-medium-blue hover:underline"
                  >
                    Edit Sessions
                  </button>
                </div>
                <div className="space-y-3">
                  {group.sessions.map((session) => {
                    const hours = getSessionHours(session);
                    const durationLabel =
                      hours >= 1
                        ? `${hours % 1 === 0 ? hours : hours.toFixed(1)} hour${hours !== 1 ? 's' : ''}`
                        : `${Math.round(hours * 60)} minutes`;
                    return (
                      <div key={session.id} className="rounded-lg border border-gray-200 p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="font-medium text-gray-900">
                              {new Date(session.start).toLocaleDateString('en-US', {
                                weekday: 'long',
                                month: 'long',
                                day: 'numeric',
                              })}
                            </p>
                            <p className="text-sm text-gray-600">
                              {formatSessionTime(session.start, session.end)} · {durationLabel}
                            </p>
                            <p className="mt-1 text-xs text-brand-medium-blue">{session.learnerLabel}</p>
                            {session.notes && (
                              <p className="mt-2 text-xs text-gray-500">Notes: {session.notes}</p>
                            )}
                          </div>
                          <span className="shrink-0 text-sm font-semibold text-gray-900">
                            ${getSessionPrice(tutorRate, session).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>

    {bookingError && (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4">
        <p className="text-sm text-red-800">{bookingError}</p>
      </div>
    )}
  </div>
);

export default BookSessionReviewStep;
